import { Button, Center, Loader, Modal, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router";
import api from "../../api";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { ROUTES } from "../../routes";
import urls from "../../urls";
import type { ExtendedUser, Priority, Todo } from "../../utils/backend-types";
import { EditTodoModal } from "./EditTodoModal/EditTodoModal";
import { TodoModal } from "./TodoModal/TodoModal";
import { CATEGORIES } from "./Todos.constants";
import { Mode } from "./Todos.types";
import type { TodoStatusCellProps } from "./TodoTable/TableColumns";
import { getBasePathByMode } from "./utils/getBasePathByMode";

interface SingleTodoProps extends Pick<TodoStatusCellProps, "onChangeStatus"> {
  todos: Array<Todo>;
  mode: Mode;
  users: Array<ExtendedUser>;
  projectId: string;
  showTodoStatus?: boolean;
  onToggleTodoModal: () => void;
  onChangeResponsible: (responsibleId: number | null, todoId: number) => void;
  onChangePriority: (priorityValue: Priority, todoId: number) => void;
  onChangeTodoStatus: (todoId: number) => void;
  onChangeDueDate: (todoId: number, dueDate: string | null) => void;
  onChangeOverdue: (todoId: number, overdue: boolean) => void;
  onChangePerson: (todoId: number, person: number) => void;
}

function SingleTodo({
  todos,
  mode,
  users,
  projectId,
  showTodoStatus,
  onToggleTodoModal,
  onChangeStatus,
  onChangeResponsible,
  onChangePriority,
  onChangeTodoStatus,
  onChangeDueDate,
  onChangeOverdue,
  onChangePerson
}: SingleTodoProps) {
  const [isEditTodoModalOpen, setIsEditTodoModalOpen] = useState(false);
  const { todoId } = useParams();

  function toggleEditTodoModal() {
    setIsEditTodoModalOpen(!isEditTodoModalOpen);
  }

  if (!todoId) {
    return null;
  }

  const basePath = getBasePathByMode(mode);
  const modalTodo = todos.find((todo) => todo.id === parseInt(todoId, 10));

  if (modalTodo) {
    // we are at /todos/todoId and the selectedTodo has been set (by clicking a Todo row)
    // or selectedTodo has never been set, meaning we arrived directly at this Todo URL
    if (isEditTodoModalOpen) {
      return (
        <EditTodoModal
          basePath={basePath}
          isOpen
          mode={mode}
          projectId={projectId}
          todo={modalTodo}
          users={users}
          variantId={modalTodo.variant}
          onToggle={toggleEditTodoModal}
        />
      );
    }

    return (
      <TodoModal
        isOpen
        mode={mode}
        showTodoStatus={showTodoStatus}
        todo={modalTodo}
        users={users}
        variantId={modalTodo.variant}
        onChangeDueDate={onChangeDueDate}
        onChangeOverdue={onChangeOverdue}
        onChangePerson={onChangePerson}
        onChangePriority={onChangePriority}
        onChangeResponsible={onChangeResponsible}
        onChangeStatus={onChangeStatus}
        onChangeTodoStatus={onChangeTodoStatus}
        onOpenEditTodoModal={toggleEditTodoModal}
        onToggle={onToggleTodoModal}
      />
    );
  }

  // we are at /todos/todoId but the todoId was invalid
  return <InvalidTodo mode={mode} projectId={projectId} />;
}

interface InvalidTodoProps {
  mode: Mode;
  projectId: string;
}

function InvalidTodo({ mode, projectId }: InvalidTodoProps) {
  const [loading, setLoading] = useState(true);
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (mode === Mode.Normal && !!params.todoId) {
      const todoId = parseInt(params.todoId, 10);

      api
        .get<Todo>(urls.api.todosDetail(todoId))
        .then((response) => {
          if (response.data.category === CATEGORIES.ONBOARDING.value) {
            document.location.href = generatePath(
              ROUTES.onboarding.singleTodo,
              {
                projectId,
                todoId: todoId.toString()
              }
            );
          } else {
            setLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [mode, projectId, params.todoId]);

  function toggle() {
    navigate("../", { replace: true });
  }

  return (
    <Modal
      className="TodoModal"
      opened
      title="Die Aufgabe existiert nicht"
      onClose={toggle}
    >
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Text>
          Die Aufgabe existiert nicht. Möglicherweise wurde sie gelöscht.
        </Text>
      )}
      <ModalFooter>
        <Button onClick={toggle}>Schließen</Button>
      </ModalFooter>
    </Modal>
  );
}

export { SingleTodo };
